import { fromLonLat } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import { Point } from 'ol/geom';
import { Feature } from 'ol';

/**
 * Añade una incidencia a la capa de incidencias pendientes
 * @param {ol/Map} map - Instancia de OpenLayers Map.
 * @param {Object} incidenceData - Datos de la incidencia del servidor
 * @param {Array} coordinates - Coordenadas [longitud, latitud]
 * @param {string} geometryType - Tipo de geometría ('Point' o 'Polygon')
 * @param {Object|null} geometry - Geometría en formato GeoJSON (opcional)
 * @returns {boolean} true si se añadió, false si no.
 */
export const addIncidenceToLayer = (map, incidenceData, coordinates, geometryType, geometry = null) => {
  if (!map) return false;

  const layer = map.getLayers().getArray().find(l => l.get('name') === 'incidencias_pendientes');
  if (!layer || typeof layer.getSource !== 'function') {
    console.warn(`⚠️ No se encontró la capa "incidencias_pendientes".`);
    return false;
  }

  const source = layer.getSource();
  // Si es un Cluster source, obtener el source interno
  const vectorSource = source.getSource ? source.getSource() : source;

  if (!vectorSource || typeof vectorSource.addFeature !== 'function') {
    console.warn(`⚠️ La capa "incidencias_pendientes" no tiene un VectorSource válido.`);
    return false;
  }

  try {
    // Crear la geometría
    let olGeometry;
    if (geometry && geometryType !== 'Point') {
      // Usar la geometría completa
      const format = new GeoJSON();
      olGeometry = format.readGeometry(geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: map.getView().getProjection()
      });
    } else {
      // Usar las coordenadas para crear un Point
      const transformedCoordinates = fromLonLat(coordinates);
      olGeometry = new Point(transformedCoordinates);
    }

    // Crear la Feature de OpenLayers directamente
    const feature = new Feature({
      geometry: olGeometry
    });

    // Campos a excluir (objetos complejos, geometrías, etc.)
    const excludedKeys = ['geometry', 'location', 'map_viewer', 'created_by', 'mapViewer', 'createdBy', 'history'];
    
    // Añadir solo propiedades primitivas de la incidencia
    Object.keys(incidenceData).forEach(key => {
      // Excluir campos complejos
      if (excludedKeys.includes(key)) {
        return;
      }
      
      const value = incidenceData[key];
      
      // Solo añadir valores primitivos (string, number, boolean, null)
      // Excluir objetos, arrays complejos, funciones
      if (value === null || 
          value === undefined ||
          typeof value === 'string' || 
          typeof value === 'number' || 
          typeof value === 'boolean') {
        feature.set(key, value);
      } else if (Array.isArray(value)) {
        // Solo arrays de primitivos
        if (value.every(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
          feature.set(key, value);
        }
      }
    });

    // Añadir geometryType
    feature.set('geometryType', geometryType);

    // Añadir la feature al source
    vectorSource.addFeature(feature);
    return true;
  } catch (error) {
    console.error('❌ Error al añadir la incidencia a la capa:', error);
    return false;
  }
};

